import type { StripeElementChangeEvent } from '@stripe/stripe-js';
import type { HostedFieldsFieldDataFields, HostedFieldsHostedFieldsFieldData } from 'braintree-web/hosted-fields';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';

import CC from 'js/lib/creditcard.util';

import { errorMapping, klarnaSupportedCountries, paymentMethods } from 'bundles/payments/common/constants';
import type {
  BillingAddress,
  BillingAddressFieldName,
  BillingInfo,
  CreditCard,
  StripeClient,
  StripeValidationErrors,
} from 'bundles/payments/common/types';
import {
  isValidAmexCVV,
  isValidCCNumberPure,
  isValidCVVPure,
  isValidExpirationDate,
  isValidPostalCode,
  isValidZipcode,
} from 'bundles/payments/lib/validate';

import _t from 'i18n!nls/payments';

type NormalFieldValidation<T> = {
  label: string;
  validator: (value: T) => boolean;
  required: boolean;
  validMeansComplete?: boolean; // true if we're OK auto-tabbing out of the field as soon as it's valid
};

// Since we don't have access to the value of a hosted field, we rely on the field status provided by the HostedFields
// object to know (a) whether or not the field is empty and (b) whether or not the field is valid.
type HostedFieldValidation<T> = NormalFieldValidation<T> & {
  getFieldStatus: () => HostedFieldsHostedFieldsFieldData;
};

type StripeValidation<T> = NormalFieldValidation<T> & {
  stripeValidation: StripeElementChangeEvent;
};

export type Validation<T = string> = NormalFieldValidation<T> | HostedFieldValidation<T> | StripeValidation<T>;

function isHostedFieldValidation<T>(validation: Validation<T>): validation is HostedFieldValidation<T> {
  return !!(validation as HostedFieldValidation<T>).getFieldStatus;
}

function isStripeValidation<T>(validation: Validation<T>): validation is StripeValidation<T> {
  return !!(validation as StripeValidation<T>).stripeValidation;
}

export const getLocationValidation = (
  billingAddress?: BillingAddress
): Partial<{ [key in BillingAddressFieldName]: Validation }> => {
  const { country } = billingAddress || {};

  const validators: Partial<{ [key in BillingAddressFieldName]: Validation }> = {
    country: {
      label: _t('Country'),
      validator: (countryVal?: string): boolean => Boolean(countryVal && countryVal.length > 1),
      required: true,
      validMeansComplete: true,
    },
  };

  if (country === 'US') {
    validators.zipcode = {
      label: _t('Zipcode'),
      validator: (zipcode: string): boolean => isValidZipcode(zipcode),
      required: true,
      validMeansComplete: true,
    };
  }
  if (country === 'CA') {
    validators.postalCode = {
      label: _t('Postal Code'),
      validator: (postalCode: string) => isValidPostalCode(postalCode, country),
      required: true,
      validMeansComplete: true,
    };
  }
  return validators;
};

export const checkValidation = <T>(
  validation?: Validation<T>,
  value?: T,
  key?: string
): { message: string | null; errorCode: string | null } => {
  let message: string | null = null;
  let errorCode: string | null = null;

  if (!validation) {
    return { message, errorCode };
  }
  const emptyMessage = _t('#{inputField} is required.', { inputField: validation.label });
  const invalidMessage = _t('#{inputField} is not correct.', { inputField: validation.label });
  const emptyErrorCode = `${key}-empty-error`;
  const invalidErrorCode = `${key}-invalid-error`;

  if (isHostedFieldValidation(validation)) {
    const fieldStatus = validation.getFieldStatus();
    if (fieldStatus.isEmpty) {
      if (validation.required) {
        message = emptyMessage;
        errorCode = emptyErrorCode;
      } // else we're good (it's empty, but not required)
    } else if (!fieldStatus.isValid) {
      message = invalidMessage;
      errorCode = invalidErrorCode;
    }
  } else if (isStripeValidation(validation)) {
    if (validation.stripeValidation.empty) {
      if (validation.required) {
        message = emptyMessage;
        errorCode = emptyErrorCode;
      } // else we're good (it's empty, but not required)
    } else if (validation.stripeValidation.error) {
      message = validation.stripeValidation.error.message;
      errorCode = invalidErrorCode;
    }
  } else {
    // Keeping the if/else inside the else block makes logical sense and is more clear.
    // eslint-disable-next-line no-lonely-if
    if (!value && validation.required) {
      message = emptyMessage;
      errorCode = emptyErrorCode;
    } else if (value && !validation.validator(value)) {
      message = invalidMessage;
      errorCode = invalidErrorCode;
    }
  }

  return { message, errorCode };
};

type CreditCardFieldsValidation = {
  cardholderName: Validation;
  cardNumber: Validation;
  expirationDate: Validation<{ month: string; year: string }>;
  cvv: Validation;
};

export const getCreditCardFieldsValidation = (
  creditCard?: CreditCard,
  braintreeHostedFieldsData?: Partial<HostedFieldsFieldDataFields>,
  stripeValidationErrors?: StripeValidationErrors
): CreditCardFieldsValidation => ({
  cardholderName: {
    label: _t('Name on Card'),
    validator: (name: string): boolean => name?.trim().length > 1,
    required: true,
  },
  cardNumber: {
    label: _t('Card Number'),
    validator: isValidCCNumberPure,
    required: true,
    ...(braintreeHostedFieldsData
      ? {
          getFieldStatus: () => braintreeHostedFieldsData.number,
        }
      : {}),
    ...(stripeValidationErrors
      ? {
          stripeValidation: stripeValidationErrors.CARD_NUMBER,
        }
      : {}),
  },
  expirationDate: {
    label: _t('Expiration date'),
    validator: isValidExpirationDate,
    required: true,
    ...(braintreeHostedFieldsData
      ? {
          getFieldStatus: () => braintreeHostedFieldsData.expirationDate,
        }
      : {}),
    ...(stripeValidationErrors
      ? {
          stripeValidation: stripeValidationErrors.CARD_EXPIRY,
        }
      : {}),
  },
  cvv: {
    label: 'CVV',
    validator:
      creditCard && creditCard.cardNumber && CC.getCardType(creditCard.cardNumber) === CC.cardTypes.amex
        ? isValidAmexCVV
        : isValidCVVPure,
    required: true,
    ...(braintreeHostedFieldsData
      ? {
          getFieldStatus: () => braintreeHostedFieldsData.cvv,
        }
      : {}),
    ...(stripeValidationErrors
      ? {
          stripeValidation: stripeValidationErrors.CARD_CVC,
        }
      : {}),
  },
});

const getCreditCardFormValidationErrors = (
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: Partial<HostedFieldsFieldDataFields>,
  returnErrorCodes = false
): Array<string | null> => {
  const messages: Array<string | null> = [];
  const errorCodes: Array<string | null> = [];
  const creditCard = billingInfo.creditCard || {};
  const { expirationMonth, expirationYear } = creditCard;
  const billingAddress = billingInfo.billingAddress || {};
  const ccFieldsValidation = getCreditCardFieldsValidation(
    billingInfo.creditCard,
    braintreeHostedFieldsData,
    billingInfo.stripeValidationErrors
  );
  const locationValidation = getLocationValidation((billingInfo || {}).billingAddress);

  Object.keys(ccFieldsValidation).forEach((key) => {
    if (key === 'expirationDate') {
      const validation = ccFieldsValidation[key];
      const value = expirationMonth && expirationYear ? { month: expirationMonth, year: expirationYear } : null;
      const { message, errorCode } = checkValidation<{ month: string; year: string }>(
        validation,
        value || undefined,
        key
      );
      messages.push(message);
      errorCodes.push(errorCode);
    } else {
      const validation = ccFieldsValidation[key as keyof Omit<CreditCardFieldsValidation, 'expirationDate'>];
      const value = creditCard[key as keyof CreditCard];
      const { message, errorCode } = checkValidation<string>(validation, value, key);
      messages.push(message);
      errorCodes.push(errorCode);
    }
  });

  Object.keys(locationValidation).forEach((key) => {
    const validation = locationValidation[key as BillingAddressFieldName];
    const value = billingAddress[key as BillingAddressFieldName]?.toString();
    const { message, errorCode } = checkValidation(validation, value, key);
    messages.push(message);
    errorCodes.push(errorCode);
  });

  return returnErrorCodes ? compact(errorCodes) : compact(messages);
};

const getBraintreeFormValidationErrors = (
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: Partial<HostedFieldsFieldDataFields>,
  returnErrorCodes = false
): Array<string | null> => getCreditCardFormValidationErrors(billingInfo, braintreeHostedFieldsData, returnErrorCodes);

const isBraintreeFormValid = (
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: Partial<HostedFieldsFieldDataFields>
) => {
  const { paymentWalletId } = billingInfo;

  if (paymentWalletId) {
    return true;
  }

  const validationErrors = getBraintreeFormValidationErrors(billingInfo, braintreeHostedFieldsData);
  return isEmpty(validationErrors);
};

const getPaypalFormValidationErrors = (billingInfo: BillingInfo, returnErrorCodes = false): Array<string | null> => {
  const messages: Array<string | null> = [];
  const errorCodes: Array<string | null> = [];
  const billingAddress = billingInfo.billingAddress || {};
  const locationValidation = getLocationValidation(billingAddress);

  Object.keys(locationValidation).forEach((key) => {
    const validation = locationValidation[key as BillingAddressFieldName];
    const value = billingAddress[key as BillingAddressFieldName]?.toString();
    const { message, errorCode } = checkValidation(validation, value, key);
    messages.push(message);
    errorCodes.push(errorCode);
  });

  return returnErrorCodes ? compact(errorCodes) : compact(messages);
};

const getStripeFormValidationErrors = (billingInfo: BillingInfo, returnErrorCodes = false): Array<string | null> =>
  getCreditCardFormValidationErrors(billingInfo, undefined, returnErrorCodes);

const isPaypalFormValid = (billingInfo: BillingInfo) => {
  const { paypalNonce, paymentWalletId } = billingInfo;

  if (paymentWalletId) {
    return true;
  }

  if (!paypalNonce) {
    return false;
  }

  const validationErrors = getPaypalFormValidationErrors(billingInfo);
  return isEmpty(validationErrors);
};

export const isStripeFormValid = (billingInfo: BillingInfo, stripeClient?: StripeClient) => {
  if (!stripeClient?.stripe || !stripeClient?.stripeElements?.getElement('cardNumber')) {
    return false;
  }

  const validationErrors = getStripeFormValidationErrors(billingInfo);
  return isEmpty(validationErrors);
};

const getPaymentElementFormValidationErrors = ({
  billingInfo,
  returnErrorCodes = false,
  skipPaymentElementErrors = false,
  locationValidationOverrides,
}: {
  billingInfo: BillingInfo;
  returnErrorCodes?: boolean;
  skipPaymentElementErrors?: boolean;
  locationValidationOverrides?: Partial<{ [key in BillingAddressFieldName]: Validation }>;
}): Array<string | null> => {
  const messages: Array<string | null> = [];
  const errorCodes: Array<string | null> = [];
  const billingAddress = billingInfo.billingAddress || {};
  const locationValidation = locationValidationOverrides ?? getLocationValidation(billingAddress);
  const stripePaymentElementErrors = billingInfo?.stripePaymentElementValidationErrors;

  Object.keys(locationValidation).forEach((key) => {
    const validation = locationValidation[key as BillingAddressFieldName];
    const value = billingAddress[key as BillingAddressFieldName]?.toString();
    const { message, errorCode } = checkValidation(validation, value, key);
    messages.push(message);
    errorCodes.push(errorCode);
  });

  if (!skipPaymentElementErrors && !isEmpty(stripePaymentElementErrors)) {
    if (
      // for 'validation_error' display the error message in error.message directly to user
      stripePaymentElementErrors?.type === 'validation_error' &&
      stripePaymentElementErrors?.code &&
      stripePaymentElementErrors?.message
    ) {
      messages.push(stripePaymentElementErrors.message);
      errorCodes.push(stripePaymentElementErrors.code);
    }
  }

  return returnErrorCodes ? compact(errorCodes) : compact(messages);
};

const getKlarnaPaymentElementFormValidationErrors = ({ billingInfo }: { billingInfo: BillingInfo }) => {
  const billingAddress = billingInfo.billingAddress || {};
  const locationValidation = getLocationValidation(billingAddress);
  const locationValidationOverrides = {
    ...locationValidation,
    ...{
      country: {
        label: _t('Country'),
        validator: (countryVal?: string): boolean =>
          Boolean(countryVal && klarnaSupportedCountries.includes(countryVal)),
        required: true,
        validMeansComplete: true,
      },
    },
  };

  return getPaymentElementFormValidationErrors({
    billingInfo,
    skipPaymentElementErrors: true,
    locationValidationOverrides,
  });
};

const isPaymentElementFormValid = (billingInfo: BillingInfo, stripeClient?: StripeClient) => {
  if (!stripeClient?.stripe || !stripeClient?.stripeElements) {
    return false;
  }
  const validationErrors = getPaymentElementFormValidationErrors({ billingInfo });
  return isEmpty(validationErrors);
};

const isAppleOrGooglePaymentElementFormValid = (billingInfo: BillingInfo, stripeClient?: StripeClient) => {
  // For Apple/Google Pay we show error message but treat PaymentElementForm as valid(skip PaymentElementError)
  if (!stripeClient?.stripe || !stripeClient?.stripeElements) {
    return false;
  }
  const validationErrors = getPaymentElementFormValidationErrors({ billingInfo, skipPaymentElementErrors: true });
  return isEmpty(validationErrors);
};

const isKlarnaPaymentElementFormValid = (billingInfo: BillingInfo, stripeClient?: StripeClient) => {
  // For Apple/Google Pay we show error message but treat PaymentElementForm as valid(skip PaymentElementError)
  if (!stripeClient?.stripe || !stripeClient?.stripeElements) {
    return false;
  }

  const validationErrors = getKlarnaPaymentElementFormValidationErrors({ billingInfo });
  return isEmpty(validationErrors);
};

export const isFormValid = (
  paymentMethod: string | undefined,
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: Partial<HostedFieldsFieldDataFields>,
  stripeClient?: StripeClient
): paymentMethod is keyof typeof paymentMethods => {
  switch (paymentMethod) {
    case paymentMethods.braintree:
      return isBraintreeFormValid(billingInfo, braintreeHostedFieldsData);
    case paymentMethods.paypal:
      return isPaypalFormValid(billingInfo);
    case paymentMethods.stripeConsumer:
      return isStripeFormValid(billingInfo, stripeClient);
    case paymentMethods.wallet:
      return !!billingInfo.paymentWalletId;
    case paymentMethods.razorpay:
      return !!billingInfo.razorpayResponse;
    case paymentMethods.zeroDollar:
      return true;
    case paymentMethods.mockPaymentProcessor:
      return true;
    case paymentMethods.card:
    case paymentMethods.pix:
      return isPaymentElementFormValid(billingInfo, stripeClient);
    case paymentMethods.apple_pay:
    case paymentMethods.google_pay:
    case paymentMethods.bancontact:
    case paymentMethods.ideal:
    case paymentMethods.sepa_debit:
    case paymentMethods.link:
      return isAppleOrGooglePaymentElementFormValid(billingInfo, stripeClient);
    case paymentMethods.klarna:
      return isKlarnaPaymentElementFormValid(billingInfo, stripeClient);
    default:
      return false;
  }
};

export const getFormValidationErrors = (
  paymentMethod: string | null | undefined,
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: HostedFieldsFieldDataFields
): Array<string | null> => {
  switch (paymentMethod) {
    case paymentMethods.braintree:
      return getBraintreeFormValidationErrors(billingInfo, braintreeHostedFieldsData);
    case paymentMethods.paypal:
      return getPaypalFormValidationErrors(billingInfo);
    case paymentMethods.stripeConsumer:
      return getStripeFormValidationErrors(billingInfo);
    case paymentMethods.card:
    case paymentMethods.pix:
      return getPaymentElementFormValidationErrors({ billingInfo });
    case paymentMethods.apple_pay:
    case paymentMethods.google_pay:
    case paymentMethods.bancontact:
    case paymentMethods.ideal:
    case paymentMethods.sepa_debit:
    case paymentMethods.link:
      return getPaymentElementFormValidationErrors({ billingInfo, skipPaymentElementErrors: true });
    case paymentMethods.klarna:
      return getKlarnaPaymentElementFormValidationErrors({ billingInfo });
    default:
      return [];
  }
};

export const getFormValidationErrorCodes = (
  paymentMethod: string | null | undefined,
  billingInfo: BillingInfo,
  braintreeHostedFieldsData?: HostedFieldsFieldDataFields
): Array<string | null> => {
  switch (paymentMethod) {
    case paymentMethods.braintree:
      return getBraintreeFormValidationErrors(billingInfo, braintreeHostedFieldsData, true);
    case paymentMethods.paypal:
      return getPaypalFormValidationErrors(billingInfo, true);
    case paymentMethods.stripeConsumer:
      return getStripeFormValidationErrors(billingInfo, true);
    case paymentMethods.card:
    case paymentMethods.apple_pay:
    case paymentMethods.google_pay:
    case paymentMethods.bancontact:
    case paymentMethods.ideal:
    case paymentMethods.sepa_debit:
    case paymentMethods.link:
    case paymentMethods.pix:
      return getPaymentElementFormValidationErrors({ billingInfo, returnErrorCodes: true });
    case paymentMethods.klarna:
      return getKlarnaPaymentElementFormValidationErrors({ billingInfo });
    default:
      return [];
  }
};

export const getFormErrorCode = (paymentMethod: string | undefined | null, billingInfo: BillingInfo): string | null => {
  switch (paymentMethod) {
    case paymentMethods.braintree:
    case paymentMethods.stripeConsumer:
      return errorMapping.invalidFields;
    case paymentMethods.card:
    case paymentMethods.apple_pay:
    case paymentMethods.google_pay:
    case paymentMethods.bancontact:
    case paymentMethods.ideal:
    case paymentMethods.sepa_debit:
    case paymentMethods.link:
    case paymentMethods.pix:
      return isEmpty(getPaymentElementFormValidationErrors({ billingInfo, skipPaymentElementErrors: true }))
        ? errorMapping.stripeValidationError
        : errorMapping.invalidFields;
    case paymentMethods.klarna:
      return isEmpty(getKlarnaPaymentElementFormValidationErrors({ billingInfo }))
        ? errorMapping.stripeValidationError
        : errorMapping.invalidFields;
    case paymentMethods.paypal:
      return billingInfo.paypalNonce ? errorMapping.invalidFields : errorMapping.paypalLogin;
    case paymentMethods.zeroDollar:
    case paymentMethods.wallet:
      return null;
    default:
      return errorMapping.generalError;
  }
};
